import React, { useState } from "react";
import { ArrowLeftRight, Plus, Trash2, Truck } from "lucide-react";
import { Modal } from "../../components/common/Modal";
import { useApp } from "../../app/context/AppContext";
import { useNotification } from "../../app/context/NotificationContext";

export function TransferModal({ isOpen, onClose }) {
  const { warehouses, products, addStockTransfer } = useApp();
  const { addToast } = useNotification();

  const [fromWh, setFromWh] = useState(warehouses[0]?.id || "WH-001");
  const [toWh, setToWh] = useState(warehouses[2]?.id || "WH-003");
  const [transferType, setTransferType] = useState("WH → Shop Godown");
  const [driverName, setDriverName] = useState("Pandurangam (Truck TS-08-UB-4412)");
  const [responsiblePerson, setResponsiblePerson] = useState("Venkateshwar Reddy");

  const [items, setItems] = useState([
    {
      productId: products[0]?.id || "",
      productName: products[0]?.name || "",
      qty: 25
    }
  ]);

  const handleProductChange = (index, prodId) => {
    const prod = products.find((p) => p.id === prodId);
    if (!prod) return;
    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      productId: prod.id,
      productName: prod.name
    };
    setItems(newItems);
  };

  const handleQtyChange = (index, qty) => {
    const newItems = [...items];
    newItems[index].qty = Math.max(1, Number(qty) || 1);
    setItems(newItems);
  };

  const addItem = () => {
    const defaultProd = products[0];
    setItems([
      ...items,
      {
        productId: defaultProd?.id || "",
        productName: defaultProd?.name || "",
        qty: 10
      }
    ]);
  };

  const removeItem = (index) => {
    if (items.length <= 1) return;
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (fromWh === toWh) {
      addToast("Origin and destination warehouse cannot be the same.", "warning");
      return;
    }

    const fromObj = warehouses.find((w) => w.id === fromWh);
    const toObj = warehouses.find((w) => w.id === toWh);

    try {
      await addStockTransfer({
        fromLocationId: fromWh,
        fromLocationName: fromObj?.name || fromWh,
        toLocationId: toWh,
        toLocationName: toObj?.name || toWh,
        type: transferType,
        driverName,
        responsiblePerson,
        items
      });
      addToast("Stock transfer dispatched into 'In Transit' state!", "success");
      onClose();
    } catch (err) {
      addToast("Failed to initiate stock transfer.", "error");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create Inter-Warehouse Stock Transfer"
      subtitle="Dispatch stock from Central Hub to Express Shop or Distribution Depots"
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4 text-xs">
        {/* Origin and Destination Route */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3.5 bg-slate-50 dark:bg-slate-950 rounded-2xl border border-slate-200 dark:border-slate-800">
          <div>
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              From Location (Origin Warehouse) *
            </label>
            <select
              value={fromWh}
              onChange={(e) => setFromWh(e.target.value)}
              className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl"
            >
              {warehouses.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              To Location (Destination Warehouse / Shop) *
            </label>
            <select
              value={toWh}
              onChange={(e) => setToWh(e.target.value)}
              className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl"
            >
              {warehouses.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Transfer Type *
            </label>
            <select
              value={transferType}
              onChange={(e) => setTransferType(e.target.value)}
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
            >
              <option value="WH → Shop Godown">WH → Shop Godown (Retail Counter)</option>
              <option value="WH → WH Distribution">WH → WH Distribution (Bulk Depot)</option>
              <option value="Shop → WH Return">Shop → WH Return (Damaged/Slow)</option>
            </select>
          </div>

          <div>
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Driver & Vehicle Info *
            </label>
            <input
              type="text"
              value={driverName}
              onChange={(e) => setDriverName(e.target.value)}
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
            />
          </div>

          <div>
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Responsible Dispatcher *
            </label>
            <input
              type="text"
              value={responsiblePerson}
              onChange={(e) => setResponsiblePerson(e.target.value)}
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
            />
          </div>
        </div>

        {/* Transfer Line Items */}
        <div className="border border-slate-200 dark:border-slate-800 rounded-2xl p-4 bg-slate-50/50 dark:bg-slate-950/40 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-bold text-slate-900 dark:text-white flex items-center gap-1.5">
              <Truck className="w-4 h-4 text-emerald-500" />
              Transfer Items Manifest ({items.length})
            </h4>
            <button
              type="button"
              onClick={addItem}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-lg shadow-sm"
            >
              <Plus className="w-3.5 h-3.5" />
              Add Product
            </button>
          </div>

          <div className="space-y-2">
            {items.map((row, idx) => (
              <div
                key={idx}
                className="grid grid-cols-1 sm:grid-cols-12 gap-2 bg-white dark:bg-slate-900 p-2.5 rounded-xl border border-slate-200 dark:border-slate-800"
              >
                <div className="sm:col-span-8">
                  <select
                    value={row.productId}
                    onChange={(e) => handleProductChange(idx, e.target.value)}
                    className="w-full px-2 py-1.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg text-xs"
                  >
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} ({p.unit})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-3">
                  <input
                    type="number"
                    min="1"
                    value={row.qty}
                    onChange={(e) => handleQtyChange(idx, e.target.value)}
                    placeholder="Qty"
                    className="w-full px-2 py-1.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-mono"
                  />
                </div>
                <div className="sm:col-span-1 flex items-center justify-center">
                  <button
                    type="button"
                    onClick={() => removeItem(idx)}
                    disabled={items.length <= 1}
                    className="text-slate-400 hover:text-rose-500 disabled:opacity-30 p-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-200 dark:border-slate-800">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 text-slate-700 dark:text-slate-300 rounded-xl"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-xl shadow-lg shadow-emerald-600/30"
          >
            Dispatch Shuttle Transfer
          </button>
        </div>
      </form>
    </Modal>
  );
}
